import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import { ReactElement, ReactNode } from 'react';
import styles from './EmblaCarousel.module.css';
import { EmblaDotButton, useEmblaDotButtons } from './EmblaCarouselPips';


interface Props {
  /** The class name to put on the DIV that conatins carousel. Use it to set the CSS variables that power the carousel:
   * 
   * --embla-carousel-gap
   * --embla-carousel-item-width
   * 
   * It can also be used to apply spacing around the carousel or set its width
   */
  emblaCarouselInfo?: UseEmblaCarouselType;
  className?: string;
  items: ReactNode[];
  /** Whether to force the items to be height: 100%. This is needed if we are not relying on the carousel items to
   * define their height. e.g. if the Carousel is sitting within a container that is height 100% then this will need to
   * be set to true
   */
  stretchItemHeight?: boolean;
  /** Whether to prevent rendering of the pips. If the pips should be visible on some screen sizes and not others then
   * leave this as false/undefined and use media queries to show/hide the pips
   */
  hidePips?: boolean;
  /** Any children that should appear within the carousel's fixed container, e.g. overlays that should be position over
   * the carousel track
   */
  children?: ReactNode;
}

/** A stupidly simple carousel, with minimal customisation */
const EmblaCarousel = (props: Props) => {
  const [emblaRef, emblaApi] = props.emblaCarouselInfo ?? useEmblaCarousel({ loop: false, duration: 10, inViewThreshold: 0.1 }, );
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useEmblaDotButtons(emblaApi);

  return (
    <div className={props.className}>
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {props.items.map((x, i) => <div className={styles.emblaItem + ' ' + (props.stretchItemHeight ? styles.emblaItemStretchHeight : '')} key={i}>{x}</div>)}
        </div>
      </div>
      {!props.hidePips &&
        <div className={styles.emblaButtonContainer}>
          {scrollSnaps.map((_, index) => (
              <EmblaDotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={index === selectedIndex ? styles.selected : ''}
              />
          ))}
        </div>
      }
      {props.children}
    </div>
  );
}

export default EmblaCarousel;